import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  TextInput,
  Button,
  FormField,
  Alert,
  Select,
  PhoneInput,
  detectDefaultPhoneCountry,
  isValidNationalPhone,
  toNationalDigits,
  type CountryCode,
} from '@vipso/shared';
import {
  useInitAppMutation,
  useLoginMutation,
  useLoginWithPasswordMutation,
} from '../api/tenantApi';
import {
  patchAuthTheme,
  setAuthFromInit,
  setPendingOrgId,
  setPendingPhone,
  setVerifyOtpResult,
} from '../store/authSlice';
import { mapInitAppPayload } from '../store/authUtils';
import type { RootState } from '../store';
import { LoginLandingLayout, loginPrimaryButtonStyle } from '../features/auth/LoginLandingLayout';
import {
  resolveDisplayLogoUrl,
  resolveTenantDefaultLogoUrl,
} from '../utils/resolveDisplayLogoUrl';

type BrandSnapshot = {
  primaryColor: string | null;
  secondaryColor: string | null;
};

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useSelector((state: RootState) => state.auth.theme);
  const config = useSelector((state: RootState) => state.auth.config);
  const tenant = useSelector((state: RootState) => state.auth.tenant);
  const tenantOrgs = useSelector((state: RootState) => state.auth.tenantOrgs);
  const lastOrgId = useSelector((state: RootState) => state.auth.lastOrgId);

  const loginType = config?.loginType === 'PASSWORD' ? 'PASSWORD' : 'OTP';
  // defaultContextLogin=true → auto/default org (no picker). false → show org list.
  const showOrgSelect = config != null && !config.defaultContextLogin;

  const tenantBrandRef = useRef<BrandSnapshot | null>(null);
  if (!tenantBrandRef.current && (theme || config)) {
    tenantBrandRef.current = {
      primaryColor: theme?.primaryColor ?? null,
      secondaryColor: theme?.secondaryColor ?? null,
    };
  }
  const tenantBrand = tenantBrandRef.current;

  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>(() =>
    detectDefaultPhoneCountry(),
  );
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [orgId, setOrgId] = useState('');
  const [login, { isLoading: otpLoading, error: otpError }] = useLoginMutation();
  const [loginWithPassword, { isLoading: pwLoading, error: pwError }] =
    useLoginWithPasswordMutation();
  const [initApp, { isLoading: initLoading }] = useInitAppMutation();

  const isLoading = otpLoading || pwLoading || initLoading;
  const error = otpError || pwError;
  const phoneValid = isValidNationalPhone(phone, phoneCountry);

  const selectedOrg = useMemo(
    () => (orgId ? tenantOrgs.find((o) => String(o.id) === String(orgId)) : undefined),
    [orgId, tenantOrgs],
  );

  const tenantSlug = tenant?.slug ?? null;
  const tenantDefaultLogo = resolveTenantDefaultLogoUrl(tenantSlug);
  const logoUrl = resolveDisplayLogoUrl(
    selectedOrg
      ? { logoFileId: selectedOrg.logoFileId, logoUrl: selectedOrg.logoUrl }
      : null,
    null,
    null,
    { tenantSlug },
  );
  const primaryColor = selectedOrg?.primaryColor || tenantBrand?.primaryColor || null;

  useEffect(() => {
    if (!showOrgSelect || orgId) return;
    if (lastOrgId && tenantOrgs.some((o) => String(o.id) === String(lastOrgId))) {
      setOrgId(String(lastOrgId));
      return;
    }
    if (tenantOrgs.length === 1) {
      setOrgId(String(tenantOrgs[0]!.id));
    }
  }, [showOrgSelect, lastOrgId, tenantOrgs, orgId]);

  // Mirror dashboard: selected org drives login brand (Mantine + logo).
  useEffect(() => {
    if (!showOrgSelect || !tenantBrand) return;
    dispatch(
      patchAuthTheme({
        primaryColor: selectedOrg?.primaryColor || tenantBrand.primaryColor,
        secondaryColor: selectedOrg?.secondaryColor || tenantBrand.secondaryColor,
        logoUrl: selectedOrg?.logoFileId
          ? selectedOrg.logoUrl ?? null
          : tenantDefaultLogo,
      }),
    );
  }, [showOrgSelect, selectedOrg, tenantBrand, tenantDefaultLogo, dispatch]);

  async function handleOtpSubmit() {
    const normalizedPhone = toNationalDigits(phone);
    if (showOrgSelect) {
      dispatch(setPendingOrgId(orgId));
    } else {
      dispatch(setPendingOrgId(null));
    }
    await login({ phone: normalizedPhone, tenantId: tenant?.id ?? null }).unwrap();
    dispatch(setPendingPhone(normalizedPhone));
    navigate('/otp');
  }

  async function handlePasswordSubmit() {
    if (showOrgSelect && !orgId) return;
    const normalizedPhone = toNationalDigits(phone);
    const result = await loginWithPassword({
      phone: normalizedPhone,
      password,
      orgId: showOrgSelect ? orgId || undefined : undefined,
      tenantId: tenant?.id ?? null,
    }).unwrap();
    const payload = result.loginWithPassword;
    dispatch(
      setVerifyOtpResult({
        user: payload.user,
        orgs: [
          {
            orgId: String(payload.orgId),
            name: payload.orgName,
            membershipId: String(payload.membershipId),
          },
        ],
      }),
    );

    const init = await initApp({
      userId: String(payload.user.id),
      orgId: String(payload.orgId),
    }).unwrap();
    dispatch(
      setAuthFromInit(mapInitAppPayload(init.initApp, { user: payload.user })),
    );
    navigate('/dashboard', { replace: true });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!phoneValid) {
      setPhoneError(t('phone.invalid'));
      return;
    }
    setPhoneError(null);
    if (showOrgSelect && !orgId) return;

    try {
      if (loginType === 'PASSWORD') {
        if (!password) return;
        await handlePasswordSubmit();
      } else {
        await handleOtpSubmit();
      }
    } catch {
      // surfaced via error state
    }
  }

  return (
    <LoginLandingLayout logoUrl={logoUrl} primaryColor={primaryColor}>
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error ? (
            <Alert color="red" title={t('auth.loginFailed')}>
              {loginType === 'PASSWORD'
                ? t('auth.invalidCredentials')
                : String(
                      (error as { data?: { message?: string }; message?: string })?.data?.message ??
                        (error as { message?: string })?.message ??
                        '',
                    )
                      .toLowerCase()
                      .includes('user not found')
                  ? t('auth.userNotFound')
                  : t('auth.otpSendFailed')}
            </Alert>
          ) : null}

          {showOrgSelect ? (
            <FormField label={t('orgPicker.organization')} required>
              <Select
                value={orgId}
                onChange={(value) => setOrgId(value || '')}
                placeholder={t('orgPicker.selectPlaceholder')}
                data={tenantOrgs.map((o) => ({
                  value: o.id,
                  label: o.name,
                }))}
                searchable
              />
            </FormField>
          ) : null}

          <FormField label={t('common.phone')} required>
            <PhoneInput
              value={phone}
              country={phoneCountry}
              placeholder={t('auth.phonePlaceholder')}
              countrySearchPlaceholder={t('phone.searchCountry')}
              error={phoneError ?? undefined}
              onChange={({ phone: nextPhone, country }) => {
                setPhone(nextPhone);
                setPhoneCountry(country);
                setPhoneError(null);
              }}
            />
          </FormField>

          {loginType === 'PASSWORD' ? (
            <FormField label={t('auth.password')} required>
              <TextInput
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                placeholder={t('auth.passwordPlaceholder')}
                type="password"
                autoComplete="current-password"
              />
            </FormField>
          ) : null}

          <Button
            type="submit"
            loading={isLoading}
            fullWidth
            disabled={!phoneValid || (showOrgSelect && !orgId)}
            style={loginPrimaryButtonStyle}
          >
            {loginType === 'PASSWORD' ? t('auth.login') : t('auth.sendOtp')}
          </Button>
        </Stack>
      </form>
    </LoginLandingLayout>
  );
}
